import api from '@/lib/api';
import type {
  PcPropertyCore,
  PcProfile,
  PcAmenityCatalogueItem,
  PcAmenitySelection,
  PcMedia,
  PcUnit,
  PcPublishGate,
  UpdateCoreBody,
  UpsertProfileBody,
  AmenitySelectionInput,
  CreateUnitBody,
  UpdateUnitBody,
  CreateMediaBody,
} from './property-content.api';

/**
 * Client for the tenant-scoped, hotel-facing listing editor
 * (apps/api .../my-property.controller.ts — all routes under /v1/my-property).
 *
 * This is the SAME shared property record the super-admin `property-content`
 * surface writes, but reached through the tenant fence: the backend aggregate is
 * a WHITELIST projection that STRIPS every internal block (KYC, contacts, PMS,
 * soft-furnishing) and every internal profile key (WiFi creds, key/after-hours
 * protocols, manager identity, team seats…). Response DTO shapes that survive
 * the projection are reused verbatim from `property-content.api.ts`.
 */

// ─── Response shapes ──────────────────────────────────────────────────────────

/** Hotel-safe subset of `PropertyContentFull` (backend `toHotelAggregate`). */
export interface MyPropertyListing {
  property: PcPropertyCore;
  /** Internal profile keys are stripped server-side; they arrive absent. */
  profile: PcProfile | null;
  amenities: {
    catalogue: PcAmenityCatalogueItem[];
    selected: PcAmenitySelection[];
  };
  media: PcMedia[];
  units: PcUnit[];
  publishGate: PcPublishGate;
  // NO contacts, kyc, pms, softFurnishing — never exposed to hotel users.
}

/** "Last edited by {actor} · {when}" header line. `null` when never edited. */
export interface MyPropertyAuditLatest {
  actorEmail: string;
  action: string;
  createdAt: string;
}

const BASE = '/my-property';

// ─── Reads ────────────────────────────────────────────────────────────────────

export async function fetchMyPropertyListing(id: string): Promise<MyPropertyListing> {
  const { data } = await api.get<MyPropertyListing>(`${BASE}/${id}/listing`);
  return data;
}

export async function fetchMyAmenityCatalogue(): Promise<PcAmenityCatalogueItem[]> {
  const { data } = await api.get<PcAmenityCatalogueItem[]>(`${BASE}/amenities/catalogue`);
  return data;
}

export async function fetchMyPropertyAuditLatest(
  id: string,
): Promise<MyPropertyAuditLatest | null> {
  const { data } = await api.get<MyPropertyAuditLatest | null>(`${BASE}/${id}/audit/latest`);
  return data;
}

export async function fetchMyUnits(id: string): Promise<PcUnit[]> {
  const { data } = await api.get<PcUnit[]>(`${BASE}/${id}/units`);
  return data;
}

// ─── Overview (F7) ──────────────────────────────────────────────────────────

/**
 * The hotel overview DTO is the guest-facing core MINUS `isActive` — the tab
 * never sends `isActive`, so the shared `UpdateCoreBody` type is safe to reuse.
 */
export async function updateMyOverview(
  id: string,
  body: UpdateCoreBody,
): Promise<MyPropertyListing> {
  const { data } = await api.patch<MyPropertyListing>(`${BASE}/${id}/overview`, body);
  return data;
}

// ─── Guidelines (F6) ──────────────────────────────────────────────────────────

/**
 * `UpsertProfileBody` is a superset of the whitelisted guidelines DTO. The
 * hotel `GuidelinesTab` (hideInternal) only ever populates whitelisted keys, so
 * an internal key can never physically reach this call.
 */
export async function updateMyGuidelines(
  id: string,
  body: UpsertProfileBody,
): Promise<PcProfile> {
  const { data } = await api.patch<PcProfile>(`${BASE}/${id}/guidelines`, body);
  return data;
}

// ─── Amenities (F5) ────────────────────────────────────────────────────────

export async function setMyAmenities(
  id: string,
  selections: AmenitySelectionInput[],
): Promise<PcAmenitySelection[]> {
  const { data } = await api.put<PcAmenitySelection[]>(`${BASE}/${id}/amenities`, {
    selections,
  });
  return data;
}

// ─── Units (F3) ────────────────────────────────────────────────────────────

export async function createMyUnit(id: string, body: CreateUnitBody): Promise<PcUnit> {
  const { data } = await api.post<PcUnit>(`${BASE}/${id}/units`, body);
  return data;
}

export async function updateMyUnit(
  id: string,
  unitId: string,
  body: UpdateUnitBody,
): Promise<PcUnit> {
  const { data } = await api.patch<PcUnit>(`${BASE}/${id}/units/${unitId}`, body);
  return data;
}

export async function deleteMyUnit(id: string, unitId: string): Promise<void> {
  await api.delete(`${BASE}/${id}/units/${unitId}`);
}

// ─── Media (F4) — file-upload only, never a URL ──────────────────────────────

export async function createMyMedia(id: string, body: CreateMediaBody): Promise<PcMedia> {
  const { data } = await api.post<PcMedia>(`${BASE}/${id}/media`, body);
  return data;
}

export async function setMyMediaCategory(
  id: string,
  mediaId: string,
  category: string | null,
): Promise<PcMedia> {
  const { data } = await api.patch<PcMedia>(`${BASE}/${id}/media/${mediaId}/category`, {
    category,
  });
  return data;
}

export async function reorderMyMedia(id: string, orderedIds: string[]): Promise<PcMedia[]> {
  const { data } = await api.patch<PcMedia[]>(`${BASE}/${id}/media/reorder`, { orderedIds });
  return data;
}

export async function setMyMediaCover(id: string, mediaId: string): Promise<PcMedia> {
  const { data } = await api.patch<PcMedia>(`${BASE}/${id}/media/${mediaId}/cover`);
  return data;
}

export async function deleteMyMedia(id: string, mediaId: string): Promise<void> {
  await api.delete(`${BASE}/${id}/media/${mediaId}`);
}
